import { useEffect } from 'react';

export default function Privacy() {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-privacy';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${import.meta.env.VITE_SITE_URL ?? ''}/privacy#webpage`,
      url: `${import.meta.env.VITE_SITE_URL ?? ''}/privacy`,
      name: 'Privacy Policy - 글로벌 이름 테스트 개인정보처리방침',
      description: '글로벌 이름 테스트 서비스의 개인정보처리방침입니다. 수집 정보, 쿠키, 광고 정책 등을 안내합니다.',
      inLanguage: 'ko',
      isPartOf: {
        '@id': `${import.meta.env.VITE_SITE_URL ?? ''}/#website`,
      },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '홈', item: import.meta.env.VITE_SITE_URL ?? '' },
          { '@type': 'ListItem', position: 2, name: 'Privacy Policy', item: `${import.meta.env.VITE_SITE_URL ?? ''}/privacy` },
        ],
      },
    });
    document.head.appendChild(script);
    return () => {
      document.getElementById('schema-privacy')?.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50">
      <div className="max-w-2xl mx-auto px-4 py-16">
        <a href="/" className="inline-flex items-center gap-2 text-purple-500 hover:text-purple-700 mb-10 cursor-pointer transition-colors">
          <i className="ri-arrow-left-line w-5 h-5 flex items-center justify-center"></i>
          <span className="text-sm font-medium">홈으로 돌아가기</span>
        </a>

        <div className="bg-white rounded-3xl shadow-xl p-10">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
          <div className="w-12 h-1 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full mb-2"></div>
          <p className="text-xs text-gray-400 mb-8">최종 수정일: 2025년 1월</p>

          <div className="space-y-8 text-gray-600 text-sm leading-relaxed">

            <section>
              <h2 className="text-base font-bold text-gray-800 mb-3">1. 수집하는 정보</h2>
              <p className="mb-2">
                본 사이트는 글로벌 이름 테스트 및 이름 궁합 기능을 제공하기 위해 사용자가 직접 입력한 이름 정보를 일시적으로 처리할 수 있습니다.
              </p>
              <ul className="list-disc list-inside space-y-1 mb-2 pl-1">
                <li>이름 궁합 기능의 경우 두 개의 이름을 입력받아 궁합 점수를 계산하는 데 사용됩니다.</li>
                <li>입력된 정보는 서비스 결과를 생성하는 과정에서만 사용되며 서버에 저장되지 않습니다.</li>
              </ul>
              <p>결과 생성 후 입력된 정보는 즉시 삭제됩니다.</p>
            </section>

            <section>
              <h2 className="text-base font-bold text-gray-800 mb-3">2. 쿠키 및 광고</h2>
              <p className="mb-2">
                본 사이트는 <strong className="text-gray-700">Google AdSense</strong>와 같은 제3자 광고 서비스를 사용할 수 있습니다.
              </p>
              <p className="mb-2">
                Google을 포함한 광고 제공업체는 쿠키를 사용하여 사용자의 이전 방문 기록을 기반으로 맞춤형 광고를 표시할 수 있습니다.
              </p>
              <p className="mb-2">
                사용자는 브라우저 설정을 통해 쿠키 사용을 거부하거나 관리할 수 있습니다.
              </p>
              <p>
                Google 광고 정책에 대한 자세한 내용은{' '}
                <a
                  href="https://policies.google.com/technologies/ads"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="text-pink-500 hover:underline cursor-pointer"
                >
                  https://policies.google.com/technologies/ads
                </a>
                에서 확인하실 수 있습니다.
              </p>
            </section>

            <section>
              <h2 className="text-base font-bold text-gray-800 mb-3">3. 제3자 서비스</h2>
              <p className="mb-2">
                본 사이트는 서비스 개선 및 트래픽 분석을 위해 Google Analytics와 같은 제3자 분석 도구를 사용할 수 있습니다.
              </p>
              <p>
                이러한 서비스에서 수집된 정보는 각 서비스 제공자의 개인정보처리방침에 따라 관리됩니다.
              </p>
            </section>

            <section>
              <h2 className="text-base font-bold text-gray-800 mb-3">4. 개인정보 보호</h2>
              <p className="mb-2">
                본 사이트는 사용자의 개인정보를 제3자에게 판매하거나 공유하지 않습니다.
              </p>
              <p>
                문의 양식을 통해 제공된 이메일 주소는 문의에 대한 답변 목적으로만 사용되며, 다른 용도로 사용되지 않습니다.
              </p>
            </section>

            <section>
              <h2 className="text-base font-bold text-gray-800 mb-3">5. 문의</h2>
              <p className="mb-3">
                개인정보 처리와 관련된 문의는 아래 이메일로 연락해 주시기 바랍니다.
              </p>
              <a
                href="mailto:naholoceo12@naver.com"
                className="inline-flex items-center gap-2 text-pink-500 font-semibold hover:text-pink-700 transition-colors cursor-pointer"
              >
                <i className="ri-mail-line w-4 h-4 flex items-center justify-center"></i>
                naholoceo12@naver.com
              </a>
            </section>

            <div className="bg-gray-50 rounded-2xl p-5 mt-6">
              <p className="text-xs text-gray-500">
                본 개인정보처리방침은 관련 법령 및 서비스 변경에 따라 업데이트될 수 있으며, 변경 사항은 본 페이지를 통해 공지됩니다.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
